import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { DashboardLayout } from '../components/DashboardLayout';
import axios from 'axios';
import {
  Brain, Calendar, Clock, User, Star, Zap, RefreshCw,
  ChevronRight, UserCheck, AlertCircle, Sparkles, Users,
  ArrowRight, CheckCircle2, Loader2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AIScheduling() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('suggest');
  const [clients, setClients] = useState([]);
  const [openShifts, setOpenShifts] = useState([]);
  const [suggestions, setSuggestions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [matchResults, setMatchResults] = useState(null);
  const [interests, setInterests] = useState([]);
  const [selectedShift, setSelectedShift] = useState(null);
  const [interestDialog, setInterestDialog] = useState(false);

  const [form, setForm] = useState({
    date: new Date().toISOString().split('T')[0],
    start_time: '09:00',
    end_time: '17:00',
    client_id: '',
    notes: ''
  });

  useEffect(() => {
    fetchClients();
    fetchOpenShifts();
  }, []);

  const fetchClients = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/users/by-org?role=client`, { withCredentials: true });
      setClients(data);
    } catch (err) { console.error(err); }
  };

  const fetchOpenShifts = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/shifts?status=open`, { withCredentials: true });
      setOpenShifts(Array.isArray(data) ? data : data.shifts || []);
    } catch (err) { console.error(err); }
  };

  const getAISuggestions = async () => {
    if (!form.date) return toast.error('Please select a date');
    setLoading(true);
    setSuggestions(null);
    try {
      const { data } = await axios.post(`${API_URL}/api/ai/schedule-suggestions`, form, { withCredentials: true });
      setSuggestions(data);
      if (data.fallback) {
        toast.info('Using rule-based suggestions (AI unavailable)');
      } else {
        toast.success('AI suggestions ready');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to get suggestions');
    }
    setLoading(false);
  };

  const autoMatchShift = async (shiftId) => {
    setLoading(true);
    setMatchResults(null);
    try {
      const { data } = await axios.post(`${API_URL}/api/shifts/auto-match/${shiftId}`, {}, { withCredentials: true });
      setMatchResults(data);
      toast.success(`Found ${data.total_matches} matches, notified ${data.notified} caregivers`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Auto-match failed');
    }
    setLoading(false);
  };

  const viewInterests = async (shiftId) => {
    try {
      const { data } = await axios.get(`${API_URL}/api/shifts/${shiftId}/interests`, { withCredentials: true });
      setInterests(data);
      setSelectedShift(shiftId);
      setInterestDialog(true);
    } catch (err) { toast.error('Failed to load interests'); }
  };

  const assignShift = async (shiftId, caregiverId, caregiverName) => {
    try {
      await axios.put(`${API_URL}/api/shifts/${shiftId}`, {
        caregiver_id: caregiverId,
        caregiver_name: caregiverName || 'Assigned',
        status: 'assigned'
      }, { withCredentials: true });
      toast.success('Caregiver assigned to shift');
      setInterestDialog(false);
      fetchOpenShifts();
    } catch (err) { toast.error(err.response?.data?.detail || 'Assignment failed'); }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-emerald-400';
    if (score >= 60) return 'text-amber-400';
    if (score >= 40) return 'text-orange-400';
    return 'text-red-400';
  };

  const getScoreBg = (score) => {
    if (score >= 80) return 'bg-emerald-500/10 border-emerald-500/20';
    if (score >= 60) return 'bg-amber-500/10 border-amber-500/20';
    if (score >= 40) return 'bg-orange-500/10 border-orange-500/20';
    return 'bg-red-500/10 border-red-500/20';
  };

  const tabs = [
    { id: 'suggest', label: 'AI Suggestions', icon: Brain },
    { id: 'match', label: 'Auto-Match', icon: Zap },
  ];

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="ai-scheduling-page">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-foreground">
            <Sparkles className="w-7 h-7 inline mr-3 text-[#00A6A0]" />AI Scheduling
          </h1>
          <p className="text-muted-foreground mt-1">Intelligent shift scheduling powered by AI</p>
        </div>

        <div className="flex gap-2 border-b border-border pb-2">
          {tabs.map(t => (
            <Button key={t.id} variant="ghost" size="sm"
              className={activeTab === t.id ? 'text-[#00A6A0] border-b-2 border-[#00A6A0] rounded-none' : 'text-muted-foreground'}
              onClick={() => setActiveTab(t.id)} data-testid={`tab-${t.id}`}>
              <t.icon className="w-4 h-4 mr-1.5" />{t.label}
            </Button>
          ))}
        </div>

        {/* AI SUGGESTION TAB */}
        {activeTab === 'suggest' && (
          <div className="space-y-6">
            <Card className="glass-card border-border">
              <CardHeader>
                <CardTitle className="text-base text-foreground flex items-center gap-2">
                  <Brain className="w-5 h-5 text-[#00A6A0]" />
                  Shift Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <label className="text-xs text-muted-foreground block mb-1">Date</label>
                    <Input type="date" value={form.date}
                      onChange={e => setForm(p => ({ ...p, date: e.target.value }))}
                      className="bg-muted border-border text-foreground"
                      data-testid="ai-date-input" />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground block mb-1">Start Time</label>
                    <Input type="time" value={form.start_time}
                      onChange={e => setForm(p => ({ ...p, start_time: e.target.value }))}
                      className="bg-muted border-border text-foreground"
                      data-testid="ai-start-time" />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground block mb-1">End Time</label>
                    <Input type="time" value={form.end_time}
                      onChange={e => setForm(p => ({ ...p, end_time: e.target.value }))}
                      className="bg-muted border-border text-foreground"
                      data-testid="ai-end-time" />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground block mb-1">Client</label>
                    <select value={form.client_id}
                      onChange={e => setForm(p => ({ ...p, client_id: e.target.value }))}
                      className="w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm h-10"
                      data-testid="ai-client-select">
                      <option value="">Any client</option>
                      {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">Additional Notes</label>
                  <Input value={form.notes}
                    onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
                    placeholder="e.g., requires dementia care experience..."
                    className="bg-muted border-border text-foreground"
                    data-testid="ai-notes-input" />
                </div>
                <Button className="btn-primary" onClick={getAISuggestions} disabled={loading}
                  data-testid="get-ai-suggestions-btn">
                  {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Brain className="w-4 h-4 mr-2" />}
                  {loading ? 'Analyzing...' : 'Get AI Suggestions'}
                </Button>
              </CardContent>
            </Card>

            {suggestions && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-heading text-foreground flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-[#00A6A0]" />
                    {suggestions.fallback ? 'Rule-Based Suggestions' : 'AI Recommendations'}
                  </h2>
                  {suggestions.fallback && (
                    <span className="text-xs px-3 py-1 rounded-full bg-amber-500/10 text-amber-400 border border-amber-500/20">
                      Fallback Mode
                    </span>
                  )}
                </div>

                {suggestions.suggestions?.length === 0 ? (
                  <Card className="glass-card border-border">
                    <CardContent className="p-8 text-center">
                      <AlertCircle className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                      <p className="text-muted-foreground">No suitable caregivers found for this shift</p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-3">
                    {suggestions.suggestions?.map((s, idx) => (
                      <Card key={s.caregiver_id || idx} className={`bg-card border ${getScoreBg(s.score)}`}
                        data-testid={`suggestion-${idx}`}>
                        <CardContent className="p-5">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <div className={`w-12 h-12 rounded-xl flex items-center justify-center font-bold text-lg ${getScoreBg(s.score)} ${getScoreColor(s.score)}`}>
                                {s.score}
                              </div>
                              <div>
                                <p className="text-foreground font-medium flex items-center gap-2">
                                  {s.caregiver_name}
                                  {idx === 0 && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#00A6A0]/10 text-[#00A6A0]">Top Pick</span>}
                                </p>
                                <div className="flex flex-wrap gap-2 mt-1">
                                  {s.reasons?.map((r, i) => (
                                    <span key={i} className="text-[10px] text-muted-foreground bg-muted px-2 py-0.5 rounded-full">{r}</span>
                                  ))}
                                </div>
                              </div>
                            </div>
                            <Button size="sm" className="bg-[#00A6A0] hover:bg-[#008A85] text-[#0F1419]"
                              onClick={() => assignShift(null, s.caregiver_id, s.caregiver_name)}
                              data-testid={`assign-suggestion-${idx}`}>
                              <UserCheck className="w-3.5 h-3.5 mr-1" />Assign
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}

                {suggestions.notes && (
                  <Card className="bg-muted border-border">
                    <CardContent className="p-4">
                      <p className="text-xs text-muted-foreground mb-1">AI Notes</p>
                      <p className="text-sm text-muted-foreground">{suggestions.notes}</p>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </div>
        )}

        {/* AUTO-MATCH TAB */}
        {activeTab === 'match' && (
          <div className="space-y-6">
            <Card className="glass-card border-border">
              <CardHeader>
                <CardTitle className="text-base text-foreground flex items-center gap-2">
                  <Zap className="w-5 h-5 text-amber-400" />
                  Open Shifts
                </CardTitle>
              </CardHeader>
              <CardContent>
                {openShifts.length === 0 ? (
                  <div className="text-center py-8">
                    <Calendar className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                    <p className="text-muted-foreground">No open shifts requiring matching</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {openShifts.map(shift => (
                      <div key={shift.id} className="flex items-center justify-between p-4 bg-muted rounded-xl border border-border"
                        data-testid={`open-shift-${shift.id}`}>
                        <div className="flex items-center gap-4">
                          <div className="p-2.5 rounded-lg bg-amber-500/10">
                            <Calendar className="w-5 h-5 text-amber-400" />
                          </div>
                          <div>
                            <p className="text-sm text-foreground font-medium">
                              {shift.date} <span className="text-muted-foreground">{shift.start_time} - {shift.end_time}</span>
                            </p>
                            <p className="text-xs text-muted-foreground">{shift.client_name || 'Unassigned client'}</p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" className="border-border text-muted-foreground text-xs"
                            onClick={() => viewInterests(shift.id)} data-testid={`view-interests-${shift.id}`}>
                            <Users className="w-3.5 h-3.5 mr-1" />Interests
                          </Button>
                          <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-[#0F1419] text-xs"
                            onClick={() => autoMatchShift(shift.id)} disabled={loading}
                            data-testid={`auto-match-${shift.id}`}>
                            {loading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Zap className="w-3.5 h-3.5 mr-1" />}
                            Auto-Match
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {matchResults && (
              <Card className="glass-card border-border">
                <CardHeader>
                  <CardTitle className="text-base text-foreground flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                    Match Results
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-4 mb-4 text-sm">
                    <span className="text-muted-foreground">Total Matches: <span className="text-[#00A6A0] font-bold">{matchResults.total_matches}</span></span>
                    <span className="text-muted-foreground">Notified: <span className="text-amber-400 font-bold">{matchResults.notified}</span></span>
                  </div>
                  {matchResults.matched_caregivers?.length > 0 ? (
                    <div className="space-y-2">
                      {matchResults.matched_caregivers.map((m, idx) => (
                        <div key={m.user_id} className="flex items-center justify-between p-3 bg-muted rounded-lg border border-border">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-lg bg-[#00A6A0]/10 flex items-center justify-center text-xs font-bold text-[#00A6A0]">
                              {idx + 1}
                            </div>
                            <div>
                              <p className="text-sm text-foreground">{m.user_name}</p>
                              <p className="text-[10px] text-muted-foreground">{m.workload} active shifts today</p>
                            </div>
                          </div>
                          <Button size="sm" className="bg-[#00A6A0] hover:bg-[#008A85] text-[#0F1419] text-xs"
                            onClick={() => assignShift(matchResults.shift_id, m.user_id, m.user_name)}
                            data-testid={`assign-match-${idx}`}>
                            <UserCheck className="w-3.5 h-3.5 mr-1" />Assign
                          </Button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted-foreground text-center py-4">No matching caregivers found</p>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Interest Dialog */}
        <Dialog open={interestDialog} onOpenChange={setInterestDialog}>
          <DialogContent className="glass-card border-border max-w-md">
            <DialogHeader>
              <DialogTitle className="text-foreground">Shift Interests</DialogTitle>
            </DialogHeader>
            {interests.length === 0 ? (
              <p className="text-muted-foreground text-center py-6">No caregivers have expressed interest yet</p>
            ) : (
              <div className="space-y-3 max-h-[400px] overflow-y-auto">
                {interests.map(i => (
                  <div key={i.id} className="flex items-center justify-between p-3 bg-muted rounded-lg border border-border">
                    <div>
                      <p className="text-sm text-foreground font-medium">{i.caregiver_name}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        {i.avg_rating && (
                          <span className="flex items-center gap-0.5 text-xs text-amber-400">
                            <Star className="w-3 h-3 fill-amber-400" />{i.avg_rating}
                          </span>
                        )}
                        <span className="text-[10px] text-muted-foreground">{i.rating_count} reviews</span>
                      </div>
                      {i.message && <p className="text-xs text-muted-foreground mt-1">{i.message}</p>}
                    </div>
                    <Button size="sm" className="bg-[#00A6A0] hover:bg-[#008A85] text-[#0F1419] text-xs"
                      onClick={() => assignShift(selectedShift, i.caregiver_id, i.caregiver_name)}
                      data-testid={`assign-interest-${i.id}`}>
                      Assign
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
